import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  AdjustContainerStockBody,
  Container,
  ContainerDetail,
  ContainerInventoryResponse,
  ContainerMovesResponse,
  ContainerSelect,
} from '@/types/backend'

async function getContainerMoves(id?: string) {
  if (!id) return null
  const { data } = await apiClient.get<ContainerMovesResponse>(
    `/containers/${id}/moves`
  )
  return data
}

async function getContainerInventory(id?: string) {
  if (!id) return null
  const { data } = await apiClient.get<ContainerInventoryResponse>(
    `/containers/${id}/inventory`
  )
  return data
}

async function adjustContainerStock(
  containerId: string | undefined,
  payload: AdjustContainerStockBody
) {
  if (!containerId) throw new Error('containerId requis')
  const { data } = await apiClient.post<{ ok: true; moveId: string }>(
    `/containers/${containerId}/_adjust`,
    payload
  )
  return data
}

export const containerQK = {
  list: (q?: { depotId?: string }) => ['containers', q ?? {}] as const,
  select: (depotId?: string) =>
    ['containers', '_select', depotId ?? 'ALL'] as const,
  one: (id: string) => ['containers', id] as const,
  moves: (id: string) => ['containers', id, 'moves'] as const,
  inventory: (id: string) => ['containers', id, 'inventory'] as const,
}

export const useContainersQuery = (
  q: { depotId?: string } = {},
  enabled = true
) =>
  useQuery({
    queryKey: containerQK.list(q),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Container[]>('/containers', {
        params: q,
      })
      return data
    },
  })

export const useContainerSelectQuery = (depotId?: string, enabled = true) =>
  useQuery({
    queryKey: containerQK.select(depotId),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ContainerSelect[]>(
        '/containers/_select',
        {
          params: { depotId },
        }
      )
      return data
    },
    staleTime: 5 * 60 * 1000,
  })

export const useContainerQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: containerQK.one(id),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ContainerDetail>(`/containers/${id}`)
      return data
    },
  })

export const useContainerMovesQuery = (
  id: string | undefined,
  enabled = true
) =>
  useQuery({
    queryKey: id ? containerQK.moves(id) : ['containers', 'moves', 'none'],
    enabled: Boolean(id) && enabled,
    queryFn: () => getContainerMoves(id),
  })

// Inventaire actuel (InventoryLevel)
export const useContainerInventoryQuery = (
  id: string | undefined,
  enabled = true
) =>
  useQuery({
    queryKey: id
      ? containerQK.inventory(id)
      : ['containers', 'inventory', 'none'],
    enabled: Boolean(id) && enabled,
    queryFn: () => getContainerInventory(id),
  })

// Ajustement (+/-)
export const useAdjustContainerStockMutation = (
  containerId: string | undefined
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AdjustContainerStockBody) =>
      adjustContainerStock(containerId, payload),
    onSuccess: () => {
      if (!containerId) return
      // rafraîchir header + mouvements + inventaire
      qc.invalidateQueries({ queryKey: containerQK.one(containerId) })
      qc.invalidateQueries({ queryKey: containerQK.moves(containerId) })
      qc.invalidateQueries({ queryKey: containerQK.inventory(containerId) })
      // si tu as des listes
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export const useCreateContainerMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Container>) => {
      const { data } = await apiClient.post<Container>('/containers', payload)
      return data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['containers'] })
      qc.setQueryData(containerQK.one(created.id), created)
      qc.invalidateQueries({ queryKey: containerQK.select() })
    },
  })
}

export const useUpdateContainerMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Container>) => {
      const { data } = await apiClient.patch<Container>(
        `/containers/${id}`,
        payload
      )
      return data
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['containers'] })
      qc.setQueryData(containerQK.one(id), updated)
      qc.invalidateQueries({ queryKey: containerQK.select() })
    },
  })
}

export const useDeleteContainerMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<Container>(`/containers/${id}`)
      return data
    },
    onSuccess: (_deleted, id) => {
      qc.invalidateQueries({ queryKey: ['containers'] })
      qc.removeQueries({ queryKey: containerQK.one(id) })
      qc.invalidateQueries({ queryKey: containerQK.select() })
    },
  })
}
